import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";

const empty = {
  title: "", authors: "", abstract: "", venue: "", year: new Date().getFullYear(),
  domain: "", doi: "", url: "", image_url: "", tags: "", featured: false,
};

export default function AdminPapersPage() {
  const [papers, setPapers] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const pager = usePager(papers, 20);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/papers", { params: { limit: 500 } });
    setPapers(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      year: Number(form.year),
      authors: form.authors.split(",").map((s) => s.trim()).filter(Boolean),
      domain: form.domain.split(",").map((s) => s.trim()).filter(Boolean),
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/papers/${editing}`, payload);
      else await api.post(`/papers`, payload);
      toast.success(editing ? "Paper updated" : "Paper added");
      setForm(empty); setEditing(null);
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const startEdit = (p) => {
    setForm({
      title: p.title, authors: p.authors.join(", "), abstract: p.abstract,
      venue: p.venue, year: p.year, domain: (p.domain || []).join(", "),
      doi: p.doi || "", url: p.url || "", image_url: p.image_url || "",
      tags: (p.tags || []).join(", "), featured: p.featured || false,
    });
    setEditing(p.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this paper?")) return;
    await api.delete(`/papers/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-papers-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PUBLISHED PAPERS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Curate <em className="italic text-kb-primary">publications</em></h1>

      <form onSubmit={save} className="mt-8 kb-card p-6 grid sm:grid-cols-12 gap-3" data-testid="admin-paper-form">
        <input className="kb-input sm:col-span-8" placeholder="Title" value={form.title} required
          onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="paper-title-input" />
        <input className="kb-input sm:col-span-2" placeholder="Year" type="number" required value={form.year}
          onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="paper-year-input" />
        <label className="sm:col-span-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="paper-featured-checkbox" />
          Featured
        </label>
        <input className="kb-input sm:col-span-8" placeholder="Authors (comma separated)" required value={form.authors}
          onChange={(e) => setForm({ ...form, authors: e.target.value })} data-testid="paper-authors-input" />
        <input className="kb-input sm:col-span-4" placeholder="Venue" required value={form.venue}
          onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="paper-venue-input" />
        <textarea className="kb-input sm:col-span-12 resize-none" rows={3} placeholder="Abstract" required value={form.abstract}
          onChange={(e) => setForm({ ...form, abstract: e.target.value })} data-testid="paper-abstract-input" />
        <input className="kb-input sm:col-span-6" placeholder="Domains (comma sep)" required value={form.domain}
          onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="paper-domain-input" />
        <input className="kb-input sm:col-span-6" placeholder="Tags (comma sep)" value={form.tags}
          onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="paper-tags-input" />
        <input className="kb-input sm:col-span-4" placeholder="DOI" value={form.doi}
          onChange={(e) => setForm({ ...form, doi: e.target.value })} data-testid="paper-doi-input" />
        <input className="kb-input sm:col-span-4" placeholder="URL" value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="paper-url-input" />
        <input className="kb-input sm:col-span-4" placeholder="Image URL" value={form.image_url}
          onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="paper-image-input" />
        <div className="sm:col-span-12 flex gap-2">
          <button className="kb-btn-primary" type="submit" data-testid="paper-save-btn"><Plus className="w-4 h-4" /> {editing ? "Update paper" : "Add paper"}</button>
          {editing && <button type="button" onClick={() => { setForm(empty); setEditing(null); }} className="kb-btn-outline">Cancel edit</button>}
        </div>
      </form>

      <div className="mt-8 border border-slate-200 rounded-sm bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-slate-200 bg-slate-50 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
          <div className="col-span-6">Title</div>
          <div className="col-span-2">Year</div>
          <div className="col-span-3">Venue</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? <div className="p-6 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
          pager.pageItems.map((p) => (
            <div key={p.id} className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-slate-200 last:border-0 items-center" data-testid={`admin-paper-row-${p.id}`}>
              <div className="col-span-12 lg:col-span-6">
                <div className="text-kb-ink font-medium line-clamp-1">{p.title}{p.featured && <span className="kb-mono text-[10px] text-amber-600 ml-2">★ FEATURED</span>}</div>
                <div className="kb-mono text-[10px] text-kb-ink-light line-clamp-1">{p.authors.join(", ")}</div>
              </div>
              <div className="col-span-4 lg:col-span-2 kb-mono text-xs text-kb-ink">{p.year}</div>
              <div className="col-span-4 lg:col-span-3 text-xs text-kb-ink-muted truncate">{p.venue}</div>
              <div className="col-span-4 lg:col-span-1 text-right space-x-2">
                <button onClick={() => startEdit(p)} className="kb-mono text-[10px] text-kb-primary" data-testid={`admin-paper-${p.id}-edit`}>EDIT</button>
                <button onClick={() => remove(p.id)} className="kb-mono text-[10px] text-rose-600" data-testid={`admin-paper-${p.id}-delete`}><Trash2 className="w-3 h-3 inline" /></button>
              </div>
            </div>
          ))}
      </div>
      <Pager {...pager} label="papers" testId="admin-papers-pager" />
    </div>
  );
}
